import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, map, tap } from 'rxjs';

import { MassUploadItem } from '#components/mass-uploader/mass-uploader.component';
import { microControllers } from '#data/microControllers.data';
import { environment } from '#environments/environment';
import { ControllerStatus } from '#models/ControllerStatus.enum';
import { ControllerCode } from '#models/MicroController.model';
import { timer } from '#utils/timer';

import { CheckResult } from './CheckResult';
import { ControllerDetails } from './ControllerDetails';
import {
  IterationStatusCode,
  LineOfBusiness,
  ReputationalRiskData,
  SupportRequestData,
  VettingIteration,
} from './VettingIteration';

const KYC_CREATION_DEFAULT_CONTROL_CODES: ControllerCode[] = [
  'CompanyGrade',
  'SensitiveBusiness',
  'SensitiveCountry',
  'EnergyPolicy',
  'EconomicSanctions',
  'PoliticalExposedPerson',
  'ReputationalRisk',
  'Gerl',
];

export function getKycCreationControlCodes(buCode: string) {
  return KYC_CREATION_DEFAULT_CONTROL_CODES.filter((controlCode) => {
    const isControlExcluded = microControllers[controlCode].isExcludedForBuCodes?.includes(buCode);
    return !isControlExcluded;
  });
}

@Injectable({
  providedIn: 'root',
})
export default class CveService {
  private readonly httpClient = inject(HttpClient);
  private readonly iterationDataSubject = new BehaviorSubject<VettingIteration | null>(null);
  public iterationData$ = this.iterationDataSubject.asObservable();
  public currentStepperIndex = 0;

  clearCurrentIteration() {
    this.iterationDataSubject.next(null);
  }

  getCustomerVettings(params: {
    lineOfBusiness?: LineOfBusiness;
    companyId?: string;
    page?: string;
    pageSize?: string;
    sort?: string;
  }) {
    return this.httpClient
      .get<
        | {
            customerVettingId: string;
            lineOfBusiness: LineOfBusiness;
            companyId: string;
            hasOpenIteration: boolean;
            iterations: { creatorId: string; iterationId: string; statusCode: IterationStatusCode }[];
          }[]
        | undefined
      >(`${environment.customerVettingUrl}/customerVettings?${new URLSearchParams(params).toString()}`)
      .pipe(map((customerVettings) => customerVettings || []));
  }

  postJourney(companyId: string, lineOfBusiness: LineOfBusiness, businessUnitCode: string, controlCodes: string[]) {
    return this.httpClient.post<{ customerVettingId: string; iterationId: string }>(
      `${environment.customerVettingUrl}/customerVettingJourney`,
      {
        companyId,
        lineOfBusiness,
        businessUnitCode,
        controlCodes,
      },
    );
  }

  getIteration(iterationId: string) {
    return this.httpClient
      .get<VettingIteration>(`${environment.customerVettingUrl}/iterations/${iterationId}`)
      .pipe(tap((data) => this.iterationDataSubject.next(data)));
  }

  async getIterations(params: {
    creatorId?: string;
    ownerId?: string;
    statusCodes?: IterationStatusCode[];
    companyId?: string;
    lineOfBusiness?: LineOfBusiness;
    page?: number;
    pageSize?: number;
    sort?: string;
    isTotalRequired?: boolean;
  }): Promise<{ iterations: VettingIteration[]; totalCount: number | null }> {
    const { statusCodes, ...uniqueParams } = params;
    const definedParams = this.omitUndefinedAndStringify(uniqueParams);
    const urlSearchParams = new URLSearchParams(definedParams);
    statusCodes?.forEach((statusCode) => urlSearchParams.append('statusCode', statusCode));
    const response = await lastValueFrom(
      this.httpClient.get<VettingIteration[] | null>(
        `${environment.customerVettingUrl}/iterations?${urlSearchParams.toString()}`,
        { observe: 'response' },
      ),
    );
    const totalItems = response.headers.get('total-items');
    return { iterations: response.body ?? [], totalCount: totalItems !== null ? +totalItems : null };
  }

  private omitUndefinedAndStringify(uniqueParams: Record<string, string | boolean | number | undefined>) {
    const entries = Object.entries(uniqueParams).reduce<[string, string][]>(
      (prev, keyValuePair) =>
        keyValuePair[1] !== undefined && keyValuePair[1] !== ''
          ? prev.concat([[keyValuePair[0], keyValuePair[1].toString()]])
          : prev,
      [],
    );
    return Object.fromEntries(entries);
  }

  attachDocumentsToIteration(iterationId: string, documentIds: string[]) {
    return this.patchIteration(iterationId, {
      documentIds,
    });
  }

  attachSupportRequestDataToIteration(
    iterationId: string,
    supportRequestData: SupportRequestData,
    supportTicketId: string,
    esgGerlQuizComment?: string,
    documentIds?: string[],
  ) {
    return this.patchIteration(iterationId, {
      customProperties: { supportRequestData, supportTicketId, esgGerlQuizComment },
      ...(documentIds?.length && { documentIds }),
    });
  }

  attachReputationalRiskDataToIteration(iterationId: string, reputationalRiskData: ReputationalRiskData) {
    return this.patchIteration(iterationId, {
      customProperties: { reputationalRiskData },
    });
  }

  patchIteration(iterationId: string, body: { documentIds?: string[]; customProperties?: Record<string, unknown> }) {
    return lastValueFrom(
      this.httpClient.patch<void>(`${environment.customerVettingUrl}/iterations/${iterationId}`, body),
    );
  }

  postIterationCheck(iterationId: string, buCode: string) {
    return this.httpClient.post<CheckResult[]>(`${environment.customerVettingUrl}/iterations/${iterationId}/check`, {
      data: { buCode },
    });
  }

  updateCurrentStepperIndex(index: number) {
    this.currentStepperIndex = index;
  }

  async updateControllerState(iterationId: string, controllerCode: ControllerCode) {
    const data = await this.getControllerState(iterationId, controllerCode);
    const currentIteration = this.iterationDataSubject.value;
    if (!currentIteration) return;

    const newIteration: VettingIteration = {
      ...currentIteration,
      controls: [...currentIteration.controls.filter((control) => control.controlCode !== controllerCode), data],
    };
    this.iterationDataSubject.next(newIteration);
    return data;
  }

  async getControllerState<T extends ControllerCode>(iterationId: string, controllerCode: T) {
    const data = await lastValueFrom(
      this.httpClient.get<ControllerDetails<T>>(
        `${environment.customerVettingUrl}/iterations/${iterationId}/controls/${controllerCode}`,
      ),
    );
    return data;
  }

  async pollControllerState(
    iterationId: string,
    controllerCode: ControllerCode,
    maxTries = 5,
    delayFactor = environment.pollingDelayFactor,
  ) {
    let isProcessing = true;
    let controllerDetails: ControllerDetails | undefined;
    for (let i = 0; i < maxTries && isProcessing; i++) {
      await timer(i * delayFactor);
      controllerDetails = await this.updateControllerState(iterationId, controllerCode);
      if (!controllerDetails) break;
      isProcessing =
        controllerDetails.statusCode === ControllerStatus.NOT_STARTED ||
        controllerDetails.statusCode === ControllerStatus.CHECKING;
    }
    return controllerDetails;
  }

  async postControllerProcessCheck<T>(iterationId: string, controllerCode: ControllerCode, data: T) {
    return this.postControllerProcess(iterationId, controllerCode, 'CHECK', data);
  }

  async postControllerProcessEscalation(
    iterationId: string,
    controllerCode: string,
    data: { comment?: string; companyId?: string; email?: string; buCode?: string },
    documentIds?: string[],
  ) {
    return this.postControllerProcess(iterationId, controllerCode, 'ESCALATION', data, documentIds);
  }

  private async postControllerProcess<T>(
    iterationId: string,
    controllerCode: string,
    processTypeCode: 'CHECK' | 'ESCALATION',
    data: T,
    documentIds?: string[],
  ) {
    return lastValueFrom(
      this.httpClient.post<CheckResult>(
        `${environment.customerVettingUrl}/iterations/${iterationId}/controls/${controllerCode}/process`,
        {
          processTypeCode,
          data,
          documentIds,
        },
      ),
    );
  }

  postRespondEscalation(
    isValidate: boolean,
    controllerCode: string,
    iterationId: string,
    escalationToken: string,
    expirationDateTime: string,
    comment?: string,
    documentIds?: string[],
  ) {
    return lastValueFrom(
      this.httpClient.post<null>(
        `${environment.customerVettingUrl}/iterations/${iterationId}/controls/${controllerCode}/escalation/respond`,
        {
          decisionCode: isValidate ? 'ACCEPTED' : 'REJECTED',
          escalationToken,
          expirationDateTime,
          comment,
          documentIds,
        },
      ),
    );
  }

  postRespondCheck(
    isValidate: boolean,
    controllerCode: string,
    iterationId: string,
    documentIds?: string[],
    responseData?: object,
  ) {
    return lastValueFrom(
      this.httpClient.post<null>(
        `${environment.customerVettingUrl}/iterations/${iterationId}/controls/${controllerCode}/check/respond`,
        {
          decisionCode: isValidate ? 'SUCCESS' : 'REJECTED',
          responseData,
          documentIds,
        },
      ),
    );
  }

  postControllerAcknowledge(iterationId: string, controllerCode: string) {
    return lastValueFrom(
      this.httpClient.post<null>(
        `${environment.customerVettingUrl}/iterations/${iterationId}/controls/${controllerCode}/acknowledge`,
        {},
      ),
    );
  }

  postCloseIteration(iterationId: string, reasonCode: 'VALIDATION' | 'CANCELLATION') {
    return lastValueFrom(
      this.httpClient.post<null>(`${environment.customerVettingUrl}/iterations/${iterationId}/close`, {
        reasonCode,
      }),
    );
  }

  postMassUpload(businessUnitCode: string, renewals: MassUploadItem[]) {
    return lastValueFrom(
      this.httpClient.post<null>(`${environment.customerVettingUrl}/safeRenewal/bulk`, { businessUnitCode, renewals }),
    );
  }
}
